import { PageHero } from "@/components/PageHero";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Activity, AlertCircle, Coffee, Moon, MessageCircle, Brain, ChevronDown, ArrowRight, Phone } from "lucide-react";
/* -------------------------------------------------------------------------- */
/*  Behavior guidance — evidence-informed (Alzheimer's Association,           */
/*  DICE approach, NIH NIA, AGS Beers Criteria, Reisberg, Cohen-Mansfield).   */
/*  Pathophysiology is summarized in caregiver language — not clinical.       */
/* -------------------------------------------------------------------------- */

const TOPICS = [
    {
        key: "agitation",
        icon: AlertCircle,
        title: "Agitation & restlessness",
        why: "Often a way of saying something — hunger, pain, fear, overstimulation, fatigue, or boredom — when words won't come.",
        pathophys: "Dementia damages the parts of the brain that filter incoming sensory information and regulate emotion (the limbic system and prefrontal cortex). When the filter weakens, ordinary sounds, lights, or even an itchy tag on a shirt can feel overwhelming. The brain registers threat without being able to explain why — and the body responds with restlessness or distress.",
        try: [
            "Lower the lights, turn off the TV, reduce background noise.",
            "Move to a quiet room or step outside for fresh air.",
            "Offer something familiar: a favorite blanket, photo, or song.",
            "Check for unmet needs: bathroom, thirst, hunger, pain, temperature.",
            "Use a soft, slow voice. Match their pace, not yours.",
            "Try gentle hand massage or a warm (not hot) cloth on the forehead.",
        ],
        why_works: "These interventions follow the DICE method (Describe-Investigate-Create-Evaluate), the most-cited evidence-based framework for behavioral symptoms. Reducing sensory input lets an overloaded brain reset; addressing unmet needs treats the actual cause — which is often physical, not psychological. Studies consistently show non-drug approaches are more effective than antipsychotics for most agitation, with far fewer harms.",
        avoid: [
            "Don't argue or correct what they say.",
            "Don't ask 'don't you remember?' — it adds shame.",
            "Don't crowd them or grab their hands suddenly.",
            "Avoid asking too many questions in a row.",
        ],
        when_to_call: [
            "New agitation that came on suddenly within hours or days — often a sign of a urinary tract infection (UTI), pneumonia, dehydration, or pain.",
            "Hitting, biting, or trying to hurt themselves or you.",
            "Refusing food or fluids for more than 24 hours.",
            "Agitation that lasts more than a few hours and won't settle with anything you try.",
        ],
        late_moment: "If you can't fix it, sit beside them. Hand on their hand. Hum a quiet tune. Presence is the intervention.",
    },
    {
        key: "wandering",
        icon: Activity,
        title: "Wandering",
        why: "May be looking for a person, place, or task that feels urgent — bathroom, work, parents, 'home' (from decades ago).",
        pathophys: "As dementia progresses, the hippocampus (the brain's GPS) shrinks. Long-term memories from 30+ years ago often stay intact longer than recent ones — which is why someone may be searching for a childhood home or a parent who's been gone for decades. They aren't 'lost' to themselves — they're trying to get back to a memory that still feels real.",
        try: [
            "Walk with them, even just down the hall. Movement helps.",
            "Validate the feeling: 'Let's go check together.'",
            "Then gently redirect: 'On the way, can you help me with this?'",
            "Install door alarms or motion sensors.",
            "Enroll in MedicAlert + Alzheimer's Safe Return (1-800-625-3780).",
            "Consider a GPS tracker (AngelSense, Apple AirTag in a shoe, etc.).",
            "Place black mats in front of exterior doors — they read as 'holes' and stop the impulse.",
        ],
        why_works: "Walking is itself a calming intervention — repetitive motion regulates the nervous system. Validation works because correcting a 70-year-old memory triggers shame and resistance; agreeing with the emotion behind it (the wanting) reduces the urgency. Door alarms and GPS are the only interventions shown to reduce serious wandering harm in randomized trials — physical restraint is associated with worse outcomes.",
        avoid: [
            "Don't say 'You can't leave.' Try 'Let's wait for [name] together.'",
            "Don't physically restrain — it can escalate to aggression and is associated with injury.",
            "Don't leave doors unlocked at night.",
            "Don't argue about whose house this is.",
        ],
        when_to_call: [
            "Any wandering event where they actually got outside or got lost — even briefly. This is a turning point conversation with the doctor.",
            "Wandering at night that's new — can signal sleep disorder or a medication issue.",
            "Wandering combined with falls, weight loss, or new confusion.",
        ],
        late_moment: "Sometimes pacing is the only release valve. Walk a slow loop with them. Don't fight it — pace alongside.",
    },
    {
        key: "sundowning",
        icon: Moon,
        title: "Sundowning & evening confusion",
        why: "Late afternoon and evening can bring more confusion, anxiety, or agitation as the day winds down.",
        pathophys: "The brain has an internal clock (the suprachiasmatic nucleus) that releases cortisol in the morning and melatonin at night. In dementia, this clock gets damaged, and the signals that say 'time to wind down' get scrambled. Combined with low light, fatigue from a long day, and reduced glucose to a tired brain, the evenings can feel destabilizing. Research suggests up to 66% of people with Alzheimer's experience some form of sundowning.",
        try: [
            "Open shades, turn on lights — keep evening rooms bright and even.",
            "Stick to a calming evening routine: same meal time, same after-dinner activity.",
            "Save complex tasks for morning when their brain is sharpest.",
            "Limit caffeine after noon. Watch sugar in the afternoon.",
            "Try a short morning walk outside — morning sunlight helps reset the body clock.",
            "Consider a daytime nap before 2pm if they're exhausted — but no later.",
        ],
        why_works: "Bright light therapy in the morning and evening has Level A evidence (Cochrane review, multiple RCTs) for stabilizing circadian rhythms in dementia. A predictable routine reduces the working memory load on a brain already running short on cognitive reserve. Limiting caffeine and afternoon sugar reduces blood-sugar spikes that worsen agitation.",
        avoid: [
            "Don't introduce new people or schedule visitors late in the day.",
            "Avoid loud TV (especially news) in the evening.",
            "Don't try to reason or 'orient them to reality' when they're confused.",
            "Avoid late dinners or heavy meals close to bedtime.",
        ],
        when_to_call: [
            "Sudden onset of sundowning that wasn't there a week ago — rule out infection or medication.",
            "Severe nightly distress that disrupts sleep for both of you for more than a week.",
            "Sundowning paired with new hallucinations or paranoia.",
        ],
        late_moment: "By late stage, sundowning may give way to long quiet hours. Soft lamp light, calm music, your steady presence — the goal becomes comfort, not orientation.",
    },
    {
        key: "repetition",
        icon: MessageCircle,
        title: "Repetitive questions or actions",
        why: "Recent memory fades while the underlying feeling — usually worry or boredom — stays. The question is a way of expressing the feeling.",
        pathophys: "The hippocampus encodes new memories before storing them long-term. In Alzheimer's, this 'save button' is damaged first — so each time they ask, it's genuinely a brand-new question to them. They have no memory of having asked it five minutes ago. Reacting as if they should know is asking a brain that physically cannot do that.",
        try: [
            "Answer calmly, like it's the first time.",
            "Write the answer on a whiteboard or notecard they can look at: 'Lunch is at noon.'",
            "Address the feeling behind it: 'I think you're worried about [...]. We're okay.'",
            "Redirect to a simple task they enjoy.",
            "If they're tracking something, use a visible clock or daily routine board.",
        ],
        why_works: "Written cues bypass the damaged memory system by using vision and recognition (which decline later) instead of recall. Addressing the underlying emotion is more effective than answering the literal question — because the question isn't really 'what time is it?', it's 'am I safe? am I forgotten?'",
        avoid: [
            "Don't sigh or say 'You just asked me that.'",
            "Don't escalate your voice — they'll mirror it.",
            "Don't try to 'teach' them the answer so they'll remember.",
        ],
        when_to_call: [
            "Repetition that's brand new and very rapid (asking same thing every 30 seconds) — could be a medication side effect.",
            "Repetitive picking at skin, lips, or clothing for hours — talk to the doctor about anxiety or boredom interventions.",
            "When repetition is so distressing to them that it's interfering with eating or sleeping.",
        ],
        late_moment: "Even into stage 6, holding their hand and saying 'I'm right here' may be all the answer needed.",
    },
    {
        key: "refusal",
        icon: AlertCircle,
        title: "Refusal of care (bathing, eating, meds)",
        why: "Care often feels invasive, undignified, or scary — especially when they don't remember why it's needed.",
        pathophys: "Two things go wrong at once. First, the brain loses the context for why this person is asking them to undress, eat, or take pills — without that context, it feels like assault. Second, the parts of the brain that filter sensory input weaken, so the temperature of water, the texture of a washcloth, or the taste of a pill can be unbearable in a way it wouldn't have been before.",
        try: [
            "Approach from the front, at eye level. Smile. Use their name.",
            "Use a calm, slow tone and short sentences.",
            "Offer a limited choice: 'Bath now or after lunch?'",
            "Warm the bathroom, towels, and the water before you start.",
            "Try washing one limb at a time — full baths can feel terrifying.",
            "For meds: try in applesauce, pudding, or yogurt (ask pharmacist about crushing).",
            "If they refuse, walk away calmly and try again in 15-30 minutes.",
        ],
        why_works: "Person-centered care principles (Kitwood) and the 'Bathing without a Battle' clinical program both show that softer, slower, choice-offering approaches reduce care resistance by 50-70%. The 15-minute reset works because the moment of refusal isn't 'remembered' — coming back later is often a brand-new interaction for them.",
        avoid: [
            "Don't surprise them — always approach where they can see you.",
            "Don't use force, even gentle force. It escalates fast.",
            "Don't take refusal personally — it's the disease, not you.",
            "Don't argue about why the care is necessary.",
        ],
        when_to_call: [
            "Refusing all food or fluids for more than 24 hours — risk of dehydration and falls.",
            "Refusing meds where missing doses is dangerous (insulin, seizure meds, blood thinners).",
            "Loss of significant weight (more than 5 lbs in a month).",
            "Refusal that's brand new — could signal pain or a UTI making the activity hurt.",
        ],
        late_moment: "In late stage, refusal often signals the body asking for rest. Let go of what doesn't matter (a missed shower won't hurt them). Save your influence for what does.",
    },
    {
        key: "aggression",
        icon: AlertCircle,
        title: "Aggression or hitting",
        why: "Aggression is almost always a response to fear, pain, or feeling cornered — not anger at you.",
        pathophys: "When the prefrontal cortex (the brain's brake on impulses) is damaged by dementia, the older 'fight or flight' parts of the brain can fire unfiltered. A care task that feels invasive, a sudden touch from behind, or unrecognized pain can flip the switch instantly. They aren't choosing to hit you — the brain skipped the step where it would normally have stopped them.",
        try: [
            "Step back. Give 6+ feet of space. Lower your voice.",
            "Validate the feeling: 'You're upset. That makes sense. I'll give you space.'",
            "Check for pain — pain is the #1 unrecognized trigger.",
            "Look for what just changed: a new room, a new caregiver, a too-tight shoe.",
            "Once calm, redirect to something soothing.",
        ],
        why_works: "Stepping back de-escalates because mirror neurons in their brain pick up YOUR calm and reflect it. Validation works for the same reason agitation responds to it: their brain registered a threat; arguing reinforces the threat, agreeing dissolves it. Treating undetected pain reduces aggression by 17% per a major 2011 BMJ trial (Husebo) — pain is that under-recognized.",
        avoid: [
            "Don't punish or scold. They won't remember the lesson, only the shame.",
            "Don't restrain them physically.",
            "Don't try to reason about what happened.",
            "Don't keep them in the same room or position — break the loop.",
        ],
        when_to_call: [
            "Any aggression that involves a weapon or risk of serious harm — go to the ER if needed.",
            "New aggression that started in the last few days — strongly suggests UTI, pain, or a medication change.",
            "Aggression more than a few times a week.",
            "When you, the caregiver, are getting hurt or feeling unsafe — this is a serious medical issue, not a personal one.",
        ],
        late_moment: "Some late-stage aggression resolves as the body weakens. Always check pain first — they may have lost the words to say where it hurts.",
    },
    {
        key: "hallucinations",
        icon: Brain,
        title: "Hallucinations & delusions",
        why: "Seeing people who aren't there, or believing things that aren't true (theft, infidelity, strangers in the house) — common in mid-to-late dementia, very real to them.",
        pathophys: "The brain stitches together what we see from raw signals. When the visual processing areas (occipital and temporal lobes) get damaged, the brain still tries to make sense of incomplete signals — and fills in the gaps. That 'person in the corner' may literally be a coat on a chair plus a brain that filled in the rest. Delusions of theft or infidelity often come from misinterpreting normal events through a brain that can't access context.",
        try: [
            "Don't argue. Their brain is showing them what's real for them.",
            "Validate the emotion: 'That sounds scary — I'm right here.'",
            "Investigate harmless triggers: mirrors, shadows, busy wallpaper, TV.",
            "Check vision and hearing — uncorrected loss makes hallucinations much worse.",
            "Reduce visual clutter. Cover mirrors at night if they're upsetting.",
            "If they're 'looking for something stolen,' offer to help them search.",
        ],
        why_works: "Validation Therapy (Feil) and reality-respecting communication consistently outperform reality-orientation for distress reduction in randomized studies. Treating uncorrected vision and hearing loss is one of the most overlooked interventions — the Lancet Commission cites hearing loss as a top modifiable dementia risk factor, and corrected hearing reduces psychotic symptoms by up to 30%.",
        avoid: [
            "Don't say 'There's no one there.' To them, there is.",
            "Don't ask them to prove it.",
            "Don't accuse them of lying.",
            "Avoid loud, busy TV — patterns can trigger visual hallucinations.",
        ],
        when_to_call: [
            "Very sudden onset of vivid hallucinations — possible delirium, infection, or new medication reaction.",
            "Hallucinations involving animals or small people — characteristic of Lewy body dementia (treatment is different — antipsychotics can be dangerous).",
            "Distressing or violent hallucinations.",
            "Any thought of suicide or self-harm.",
        ],
        late_moment: "In late stage, sit close, hold their hand, and just be in the moment with them. Whatever they're seeing, your presence is the anchor.",
    },
    {
        key: "paranoia",
        icon: Brain,
        title: "Paranoia & suspicion",
        why: "Accusations of theft, infidelity, or strangers in the house can blindside you — but they're a known symptom of dementia, not a judgment on you.",
        pathophys: "Paranoid delusions come from a brain that can no longer hold context together. They put their wallet somewhere, can't retrieve the memory of where, and the only explanation their brain can build is 'someone took it.' They don't recognize a spouse's face after a nap, so the person in their bed must be an imposter or an affair. The hippocampus and frontal lobes — which normally cross-check 'wait, that doesn't make sense' — are damaged, so the suspicion lands as certainty. Up to 40% of people with Alzheimer's experience paranoid delusions at some point, most commonly in mid-stage (FAST 5–6).",
        try: [
            "Don't argue or try to prove they're wrong — to them, it is real.",
            "Validate the feeling first: 'That sounds upsetting. I'm sorry you're scared.'",
            "Help them search for the 'stolen' item — finding it together resolves the moment.",
            "Keep duplicates of frequently misplaced items (glasses, wallet, keys) in obvious spots.",
            "Reduce overheard triggers: turn off news, don't whisper near them, don't have multiple people talking at once.",
            "If they don't recognize you: stay calm, step out for a minute, come back and reintroduce yourself by name.",
            "Check medications with the doctor — some (anticholinergics, steroids, sleep meds) worsen paranoia.",
        ],
        why_works: "Validation Therapy (Naomi Feil) and the DICE method both treat the underlying emotion — fear and loss of control — rather than the literal accusation, and consistently reduce distress more than reality-orientation. Searching together neutralizes the threat narrative because their brain registers the partnership as 'we're a team, not enemies.' Reviewing meds matters because anticholinergic burden (the Beers Criteria list) is a top reversible cause of new paranoia — discontinuing offending meds resolves symptoms in up to 30% of cases.",
        avoid: [
            "Don't say 'No one stole anything' — it confirms their fear that no one believes them.",
            "Don't laugh, even gently. Paranoia feels like an emergency to them.",
            "Don't try to convince them you're you. Reasoning with damaged reasoning makes both of you tired.",
            "Don't take accusations personally — the accusation is the disease talking.",
            "Don't bring up old paranoia episodes 'to remind them.' Each time, it's new to them.",
        ],
        when_to_call: [
            "Paranoia that started suddenly in the last few days — UTI, infection, or new medication is the likely cause until proven otherwise.",
            "Capgras-style delusions (insisting you or another loved one is an imposter) — this needs a doctor's evaluation; antipsychotics may help but Lewy body dementia must be ruled out first.",
            "Paranoia that leads to refusing food (afraid it's poisoned), refusing meds, or refusing to let the caregiver near them.",
            "Any thought of hurting themselves or another person.",
            "Paranoia paired with new aggression or hallucinations.",
        ],
        late_moment: "By late stage, paranoia often softens as language fades. Your steady presence — the same chair, the same voice, the same hand — becomes the proof of safety their brain can still read.",
    },
    {
        key: "appetite",
        icon: Coffee,
        title: "Appetite & weight changes",
        why: "Both losing interest in food and craving sweets/eating non-foods are common as dementia changes how taste, smell, and hunger signals work.",
        pathophys: "The frontal and temporal lobes regulate appetite, taste recognition, and the impulse to eat. Damage here can mute hunger signals, blunt taste (so food feels bland), or remove the brake that stops constant snacking. Loss of smell often comes first and makes food taste like cardboard. Late stage: the brain's ability to coordinate chewing and swallowing weakens — known as dysphagia.",
        try: [
            "Offer small, frequent, finger-food meals.",
            "Boost flavor: a little salt, butter, or sweet sauce can re-engage interest.",
            "Eat together. Mirror-eating helps — they often follow your lead.",
            "Use a bright, plain plate (white food on white plate disappears to them).",
            "Reduce distractions: TV off, simple table.",
            "If swallowing is hard: thicker liquids, smaller bites, sit upright for 30 min after.",
        ],
        why_works: "Small frequent meals work because dementia reduces sustained attention — they get tired mid-meal. Color contrast plates have shown to increase food intake by up to 25% in studies of late-stage dementia. Treating swallowing issues early (with a speech therapist's evaluation) prevents aspiration pneumonia — a leading cause of death in advanced dementia.",
        avoid: [
            "Don't force-feed. It can cause choking and panic.",
            "Don't comment on weight in a way that adds shame.",
            "Don't offer unlimited choices — overwhelms a tired brain.",
            "Avoid thin liquids if there's any coughing while drinking.",
        ],
        when_to_call: [
            "Coughing or choking while eating or drinking — needs a swallow evaluation.",
            "Weight loss of more than 5% in a month.",
            "Refusing food entirely for more than 24 hours.",
            "Eating non-food items (paper, soap, etc.) — could need additional supervision.",
        ],
        late_moment: "Late stage, the body needs less. A few sips, a few bites — this is often what they can handle, and it's enough.",
    },
];

export default function Guidance() {
    const [open, setOpen] = useState("");

    return (
        <div className="space-y-10 animate-fade-in-up">
            <PageHero
                kicker="Behavior guidance"
                kickerIcon={Brain}
                title="The why behind the what."
                subtitle={<>Behavior is communication. Each section below explains what&apos;s happening in the brain, what helps, and when to call the doctor — informed by research, written for caregivers.</>}
            />

            <section className="ml-card p-6 md:p-8 bg-teal-soft !border-teal-soft">
                <h2 className="text-xl font-heading font-medium text-teal-dark mb-2">Three things to remember</h2>
                <ul className="space-y-2.5 text-[#1F2624]">
                    <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span><span><strong>The brain, not the person:</strong> behavior changes come from physical damage to specific brain regions — they aren&apos;t being difficult on purpose.</span></li>
                    <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span><span><strong>New behavior = look for new causes:</strong> a sudden change usually means infection, pain, dehydration, or a medication issue. Always rule these out first.</span></li>
                    <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span><span><strong>You can&apos;t reason it away:</strong> validation almost always works better than logic. Their reality is real to them.</span></li>
                </ul>
            </section>

            {/* Topic accordions */}
            <section className="space-y-4" data-testid="guidance-topics">
                {TOPICS.map(t => (
                    <article key={t.key} className="ml-card overflow-hidden" data-testid={`guidance-${t.key}`}>
                        <button
                            onClick={() => setOpen(open === t.key ? "" : t.key)}
                            className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                            data-testid={`guidance-toggle-${t.key}`}
                        >
                            <div className="flex items-center gap-3.5">
                                <span className="w-10 h-10 rounded-2xl bg-ml_blue-light text-ml_blue-dark flex items-center justify-center shrink-0">
                                    <t.icon strokeWidth={1.6} size={18} />
                                </span>
                                <h3 className="text-lg md:text-xl font-heading font-medium text-[#1F2624]">{t.title}</h3>
                            </div>
                            <ChevronDown size={20} strokeWidth={1.6}
                                className={`transition-transform shrink-0 ${open === t.key ? "rotate-180" : ""}`} />
                        </button>

                        {open === t.key && (
                            <div className="px-5 md:px-6 pb-7 border-t border-[#E2E8F0] pt-5 space-y-5">
                                {/* Why this happens — short */}
                                <p className="text-[#1F2624] leading-relaxed bg-cream-dark/60 rounded-xl p-4">
                                    <strong className="text-teal">In plain terms: </strong>{t.why}
                                </p>

                                {/* Pathophysiology — light clinical depth */}
                                {t.pathophys && (
                                    <div className="bg-ml_blue-light/40 rounded-xl p-4 border border-ml_blue-light"
                                        data-testid={`guidance-pathophys-${t.key}`}>
                                        <p className="text-sm font-semibold uppercase tracking-wider text-ml_blue-dark mb-2 flex items-center gap-2">
                                            <Brain strokeWidth={1.8} size={14} /> What&apos;s happening in the brain
                                        </p>
                                        <p className="text-[#1F2624] leading-relaxed">{t.pathophys}</p>
                                    </div>
                                )}

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <h4 className="text-sm font-semibold uppercase tracking-wider text-teal mb-2.5">Try this</h4>
                                        <ul className="space-y-2">
                                            {t.try.map((x, i) => (
                                                <li key={i} className="flex gap-2.5 text-[#1F2624]">
                                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span>
                                                    <span className="leading-relaxed">{x}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold uppercase tracking-wider text-stage_middle-dark mb-2.5">Avoid</h4>
                                        <ul className="space-y-2">
                                            {t.avoid.map((x, i) => (
                                                <li key={i} className="flex gap-2.5 text-[#1F2624]">
                                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-stage_middle-dark shrink-0"></span>
                                                    <span className="leading-relaxed">{x}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                {/* Why it works — evidence */}
                                {t.why_works && (
                                    <div className="bg-stage_early/50 rounded-xl p-4 border border-stage_early"
                                        data-testid={`guidance-why-works-${t.key}`}>
                                        <p className="text-sm font-semibold uppercase tracking-wider text-stage_early-dark mb-2">
                                            Why this works (the evidence)
                                        </p>
                                        <p className="text-[#1F2624] leading-relaxed">{t.why_works}</p>
                                    </div>
                                )}

                                {/* When to call provider */}
                                {t.when_to_call && (
                                    <div className="bg-coral-light/60 rounded-xl p-4 border border-coral-light"
                                        data-testid={`guidance-call-${t.key}`}>
                                        <p className="text-sm font-semibold uppercase tracking-wider text-coral-dark mb-2 flex items-center gap-2">
                                            <Phone strokeWidth={1.8} size={14} /> When to call the doctor
                                        </p>
                                        <ul className="space-y-1.5">
                                            {t.when_to_call.map((x, i) => (
                                                <li key={i} className="flex gap-2.5 text-[#1F2624]">
                                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-coral-dark shrink-0"></span>
                                                    <span className="leading-relaxed">{x}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Late-stage moment */}
                                <div className="bg-stage_late/50 rounded-xl p-4 border border-stage_late">
                                    <p className="text-sm font-semibold uppercase tracking-wider text-stage_late-dark mb-1.5 flex items-center gap-2">
                                        <Brain strokeWidth={1.8} size={14} /> In the late-stage moment
                                    </p>
                                    <p className="text-[#1F2624] leading-relaxed">{t.late_moment}</p>
                                </div>

                                {/* Talk to Lane CTA */}
                                <Link
                                    to={`/chat?prompt=${encodeURIComponent(`I'm dealing with ${t.title.toLowerCase()} right now. Can you help me think through what to try?`)}`}
                                    data-testid={`guidance-lane-${t.key}`}
                                    className="ml-btn-secondary inline-flex"
                                >
                                    <MessageCircle size={16} strokeWidth={1.6} /> Talk to Lane about this
                                </Link>
                            </div>
                        )}
                    </article>
                ))}
            </section>

            <section className="ml-card p-5 md:p-6 bg-cream-dark/60 !border-cream-dark" data-testid="guidance-sources">
                <p className="text-xs text-[#4C5551]">
                    <strong>About the guidance:</strong> sections marked &quot;the evidence&quot; reference
                    peer-reviewed research and clinical frameworks (DICE, Validation Therapy, Bathing Without
                    a Battle, AGS Beers Criteria, Lancet Commission on Dementia, Cochrane reviews). This is
                    education, not personalized medical advice — always check with your loved one&apos;s doctor.
                </p>
            </section>

            <section className="ml-card p-6 md:p-8 bg-ml_blue-light !border-ml_blue-light">
                <h2 className="text-xl font-heading font-medium text-ml_blue-dark mb-2">Track what happens, share with the doctor.</h2>
                <p className="text-[#1F2624]">
                    Patterns matter more than any single moment. Log behaviors and bring the summary to your next appointment.
                </p>
                <Link to="/behavior" data-testid="guidance-to-tracker" className="ml-btn-secondary mt-5 inline-flex">
                    Open Behavior Tracker <ArrowRight size={16} strokeWidth={1.8} />
                </Link>
            </section>

            {/* Clarity Call card removed (Jul 2026, founder decision):
                the caregiver side gives — no call-scheduling asks. */}
        </div>
    );
}
